<template>
  <div class="country-selector">
    <div class="selector-label">
      <i class="fas fa-globe-americas mr-2"></i>
      <span>Select Country</span>
    </div>
    <div class="selector-wrapper">
      <div class="custom-select" :class="{ 'is-open': isOpen }">
        <div class="select-trigger" @click="toggleDropdown">
          <span v-if="selectedCountry" class="selected-option">
            <span class="country-flag">{{ getCountryFlag(selectedCountry.code) }}</span>
            <span class="country-name">{{ selectedCountry.name }}</span>
          </span>
          <span v-else class="placeholder">Choose a country...</span>
          <i class="fas fa-chevron-down dropdown-icon"></i>
        </div>

        <transition name="dropdown">
          <div v-if="isOpen" class="options-list">
            <div class="search-box">
              <i class="fas fa-search search-icon"></i>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search countries..."
                class="search-input"
                @click.stop
              />
            </div>
            <div class="options-scroll">
              <div
                v-for="country in filteredCountries"
                :key="country.code"
                class="option-item"
                :class="{ 'selected': selectedCountry && selectedCountry.code === country.code }"
                @click="selectCountry(country)"
              >
                <span class="country-flag">{{ getCountryFlag(country.code) }}</span>
                <span class="country-name">{{ country.name }}</span>
                <span class="country-code">{{ country.code }}</span>
              </div>
              <div v-if="filteredCountries.length === 0" class="no-results">
                No countries found
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CountrySelector',
  props: {
    modelValue: {
      type: String,
      default: null
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      isOpen: false,
      searchQuery: '',
      countries: [
        { code: 'US', name: 'United States' },
        { code: 'FR', name: 'France' },
        { code: 'ES', name: 'Spain' },
        { code: 'IT', name: 'Italy' },
        { code: 'DE', name: 'Germany' },
        { code: 'GB', name: 'United Kingdom' },
        { code: 'JP', name: 'Japan' },
        { code: 'CN', name: 'China' },
        { code: 'BR', name: 'Brazil' },
        { code: 'IN', name: 'India' },
        { code: 'MX', name: 'Mexico' },
        { code: 'CA', name: 'Canada' },
        { code: 'AU', name: 'Australia' },
        { code: 'RU', name: 'Russia' },
        { code: 'KR', name: 'South Korea' },
        { code: 'AR', name: 'Argentina' },
        { code: 'EG', name: 'Egypt' },
        { code: 'ZA', name: 'South Africa' },
        { code: 'NG', name: 'Nigeria' },
        { code: 'TR', name: 'Turkey' },
        { code: 'ID', name: 'Indonesia' },
        { code: 'TH', name: 'Thailand' },
        { code: 'PL', name: 'Poland' },
        { code: 'NL', name: 'Netherlands' },
        { code: 'BE', name: 'Belgium' },
        { code: 'SE', name: 'Sweden' },
        { code: 'NO', name: 'Norway' },
        { code: 'DK', name: 'Denmark' },
        { code: 'FI', name: 'Finland' },
        { code: 'PT', name: 'Portugal' },
        { code: 'GR', name: 'Greece' },
        { code: 'CH', name: 'Switzerland' },
        { code: 'AT', name: 'Austria' },
        { code: 'IE', name: 'Ireland' },
        { code: 'NZ', name: 'New Zealand' },
        { code: 'SG', name: 'Singapore' },
        { code: 'MY', name: 'Malaysia' },
        { code: 'PH', name: 'Philippines' },
        { code: 'VN', name: 'Vietnam' },
        { code: 'CL', name: 'Chile' },
        { code: 'CO', name: 'Colombia' },
        { code: 'PE', name: 'Peru' },
        { code: 'VE', name: 'Venezuela' },
        { code: 'SA', name: 'Saudi Arabia' },
        { code: 'AE', name: 'United Arab Emirates' },
        { code: 'IL', name: 'Israel' },
        { code: 'UA', name: 'Ukraine' },
        { code: 'RO', name: 'Romania' },
        { code: 'CZ', name: 'Czech Republic' },
        { code: 'HU', name: 'Hungary' }
      ].sort((a, b) => a.name.localeCompare(b.name))
    }
  },
  computed: {
    selectedCountry() {
      if (!this.modelValue) return null
      return this.countries.find(c => c.code === this.modelValue)
    },
    filteredCountries() {
      if (!this.searchQuery) return this.countries
      const query = this.searchQuery.toLowerCase()
      return this.countries.filter(country =>
        country.name.toLowerCase().includes(query) ||
        country.code.toLowerCase().includes(query)
      )
    }
  },
  mounted() {
    document.addEventListener('click', this.closeDropdown)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeDropdown)
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.searchQuery = ''
      }
    },
    closeDropdown(e) {
      if (!this.$el.contains(e.target)) {
        this.isOpen = false
      }
    },
    selectCountry(country) {
      this.$emit('update:modelValue', country.code)
      this.$emit('change', country.code)
      this.isOpen = false
      this.searchQuery = ''
    },
    getCountryFlag(countryCode) {
      if (!countryCode || countryCode.length !== 2) return '🌍'
      const codePoints = countryCode
        .toUpperCase()
        .split('')
        .map(char => 127397 + char.charCodeAt())
      return String.fromCodePoint(...codePoints)
    }
  }
}
</script>

<style scoped>
.country-selector {
  width: 100%;
  max-width: 400px;
}

.selector-label {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6b7280;
  margin-bottom: 8px;
}

.custom-select {
  position: relative;
  width: 100%;
}

.select-trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.select-trigger:hover {
  border-color: #667eea;
}

.is-open .select-trigger {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.selected-option {
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  color: #1f2937;
}

.placeholder {
  color: #9ca3af;
}

.country-flag {
  font-size: 1.25rem;
}

.country-name {
  font-size: 0.9375rem;
}

.dropdown-icon {
  color: #6b7280;
  transition: transform 0.3s ease;
}

.is-open .dropdown-icon {
  transform: rotate(180deg);
}

.options-list {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  overflow: hidden;
}

.search-box {
  position: relative;
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.search-icon {
  position: absolute;
  left: 24px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.search-input {
  width: 100%;
  padding: 8px 12px 8px 32px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 0.875rem;
  outline: none;
  transition: border-color 0.3s ease;
}

.search-input:focus {
  border-color: #667eea;
}

.options-scroll {
  max-height: 300px;
  overflow-y: auto;
}

.option-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.option-item:hover {
  background-color: #f9fafb;
}

.option-item.selected {
  background-color: #ede7f6;
  color: #667eea;
  font-weight: 600;
}

.option-item .country-name {
  flex: 1;
}

.option-item .country-code {
  font-size: 0.75rem;
  color: #9ca3af;
  font-weight: 600;
}

.no-results {
  padding: 24px 16px;
  text-align: center;
  color: #9ca3af;
  font-size: 0.875rem;
}

/* Transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.3s ease;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .country-selector {
    max-width: 100%;
  }

  .select-trigger {
    padding: 10px 14px;
  }

  .country-flag {
    font-size: 1.125rem;
  }

  .country-name {
    font-size: 0.875rem;
  }

  .options-scroll {
    max-height: 250px;
  }
}
</style>
